import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, withCtx as _withCtx, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode } from "vue"

import { alertController, IonButton, IonIcon, modalController } from "@ionic/vue";
import { computed, nextTick, onMounted, ref } from "vue";
import { route } from "@/router";
import { t } from "@/plugins/vue-i18n";

import ModalControllerBase from "@/components/modal/modalControllerModals/ModalControllerBase.vue";
import CustomHeader from "@/components/basePageElements/CustomHeader.vue";
import InformationInterface from "@/components/modal/InformationInterface.vue";
import ButtonNavigations from "@/components/button/ButtonNavigations.vue";

import { useListStore } from "@/stores/lists";
import { useSupplierStore } from "@/stores/suppliers";
import { useNotificationStore } from "@/stores/notification";

import { interactionFeedbackToast } from "@/functions/baseToast";
import { popAlertOk } from "@/functions/alert";
import { isApp } from "@/functions/enviroment";

import { Information, Promotion, PromotionOld } from "@/types/notification";
import { ProductMaster, Supplier } from "@/types/supplier";
import { IonAlertConfig } from "@/types/ionic";
import { AUDIENCE_TYPE, INFORMATION_TYPE, TRACKING_EVENT } from "@/types/enum/notification";
import { AddInformationCartParams } from "@/types/list";

import { chevronBack } from "ionicons/icons";
import DashboardPromotionProductOld from "@/components/modal/modalControllerModals/DashboardPromotionProductOld.vue";


export default /*@__PURE__*/_defineComponent({
  __name: 'DashboardPromotionProduct',
  props: {
    information: {},
    itemPlaceholder: { type: Boolean },
    noButtons: { type: Boolean }
  },
  setup(__props: any) {

const props = __props

const listStore = useListStore()
const supplierStore = useSupplierStore()
const notificationStore = useNotificationStore()

const locationId = computed<string>(() => String(route.value.params.locationId ?? ''))
const isButtonBack = computed<boolean>(() => notificationStore.currentInformationIndex > 0)
const isButtonForward = computed<boolean>(() => notificationStore.currentInformationIndex < (notificationStore.allPromotions.length - 1))
const currentInformation = computed<Promotion | undefined>(() => {
    if (props.information) return props.information
    if (!notificationStore.currentPromotion || notificationStore.currentPromotion.type === 'old') return undefined
    return notificationStore.currentPromotion.item
})

const addToCart = async (data: { product: ProductMaster, amount: number }): Promise<void> => {
    if (!data.product.id || !currentInformation.value) return;

    const supplierList: Supplier[] = []

    if (currentInformation.value.audience.type === AUDIENCE_TYPE.ALL) {
        supplierList.push(...supplierStore.suppliers.data)
    } else {
        if (!currentInformation.value.audience.participatingSupplierIds?.length) return await popAlertOk({
            message: t('alert.noSupplier')
        })

        for (const participatorId of currentInformation.value.audience.participatingSupplierIds) {
            const supplier = supplierStore.suppliers.data.find((supplierEl: Supplier) => supplierEl.id === participatorId);
            if (!supplier) continue;

            // remove suppliers from the selection alert who do not have harmonized products
			const productAvailableBySupplier = data.product.availableBy?.includes(supplier.id);
			if (!productAvailableBySupplier) continue;
            
            supplierList.push(supplier);
        }
    }

	/*
	DONE: Remove suppliers from the selection alert who do not have harmonized products.
	DONE: Remove product variant from the selection if there are no harmonized product by any supplier of this location
    CHECK: Force to show alert with only one supplier (who has selected product variant)?
	TODO / CHECK: Remove promotion from location if there are no harmonised products by any supplier of this location (empty promotion)?
	*/

    function checkLimitOrderSize (supplierId: string) {
        const existInCart = listStore.promotionProductInCart(supplierId, data.product.id)
        const cartQuantity = existInCart ? existInCart.quantity : 0;

        const { stepAmount, limitOrderSize } = data.product;
        const quantity = data.amount
        // Check if the SUM of this order and the order from the promotion section is less than or equal to the LIMIT ORDER SIZE.
        if (stepAmount && limitOrderSize && (stepAmount + cartQuantity > limitOrderSize)) {
            interactionFeedbackToast(
                false,
                { type: 'promotionLimitReached', id: 'promotion-limit-reached'},
                { cssClass: 'toast-offset-70' }
            )
            return // break function at this step
        }
        
        const maxAvailableQty = limitOrderSize && ((quantity + cartQuantity) > limitOrderSize)
            ? limitOrderSize - cartQuantity
            : quantity

        if (quantity > maxAvailableQty) {
            popAlertOk({
                message: t('alert.orderAmountOverLimit', { isAmount: cartQuantity, availableAmount: maxAvailableQty }),
                handler: async () => {
                    if (currentInformation.value) addInformationItemToCart({
                        locationId: locationId.value,
                        informationId: currentInformation.value.id,
                        productId: data.product.id,
                        quantity: maxAvailableQty,
                        supplierId: supplierId
                    })
                    alertController.dismiss()
                },
            })
            return // break function at this step
        }

        if (currentInformation.value) addInformationItemToCart({
            locationId: locationId.value,
            informationId: currentInformation.value.id,
            productId: data.product.id,
            quantity: data.amount,
            supplierId: supplierId
        })
    }

    if (supplierList.length === 1 /* && initialSupplierListLength === 1 */) {
        checkLimitOrderSize(supplierList[0].id)
        return
    }

    const params: IonAlertConfig = {
        header: t('alert.selectSupplier'),
        buttons: [{
            text: t('button.ok'),
            handler: (value: string) => {
                if (!value) return false
                checkLimitOrderSize(value)
            }
        }],
        cssClass: 'custom-alert',
        inputs: [],
    }

    for (const supplier of supplierList) {
        if (supplier.id) params.inputs.push({
            label: supplier.name,
            type: 'radio',
            value: supplier.id
        })
    }
    const alert = await alertController.create(params);
    await alert.present();
}

const addInformationItemToCart = async (params: AddInformationCartParams): Promise<void> => {
    if (!params.productId) return;

    await modalController.dismiss()

    const response = await listStore.addInformationItemToCart(params)

    await listStore.getShoppingCart(locationId.value)

    if (response) await interactionFeedbackToast(response, {
        type: 'addCart',
        id: 'add-product-cart'
    }, {
        cssClass: 'toast-offset-50'
    })
}

const addInformationPackageToCart = async (value: { products: Record<string, number>, supplierId: string }): Promise<void> => {
    const { supplierId, products } = value;
    if (!supplierId || !Object.keys(products ?? {}).length) return;

    await modalController.dismiss()

    if (currentInformation.value) {
        let successfullyAdded = 0;
        for (const [ productId, quantity ] of Object.entries(products)) {
            const response = await listStore.addInformationItemToCart({
                locationId: locationId.value,
                informationId: currentInformation.value.id,
                productId: productId,
                quantity: quantity,
                supplierId: supplierId
            })

            if (response) successfullyAdded++
        }
        
        await interactionFeedbackToast(successfullyAdded === Object.keys(products).length, {
            type: 'addPromoPackage',
            id: 'add-product-cart',
        }, {
            cssClass: 'toast-offset-50'
        })
    }

    await listStore.getShoppingCart(locationId.value)
}

const openOldPromotion = async () => {
    await (await modalController.create({
        component: DashboardPromotionProductOld
    })).present()
}

const goBack = async (): Promise<void> => {
    if (isButtonBack.value) {
        const previousElement = notificationStore.allPromotions[notificationStore.currentInformationIndex - 1]

        if (!previousElement) return

        if (previousElement.type === 'old') {
            await modalController.dismiss()
            notificationStore.currentInformationIndex -= 1
            await openOldPromotion()
            return
        }

        notificationStore.currentInformationIndex -= 1
        nextTick(sendTracking)
    }
}

const goFoward = async (): Promise<void> => {
    if (isButtonForward.value) {
        const nextElement = notificationStore.allPromotions[notificationStore.currentInformationIndex + 1]

        if (!nextElement) return

        if (nextElement.type === 'old') {
            await modalController.dismiss()
            notificationStore.currentInformationIndex += 1
            await openOldPromotion()
            return
        }

        notificationStore.currentInformationIndex += 1
        nextTick(sendTracking)
    }
}

const sendTracking = () => {
    if (!currentInformation.value) return
    notificationStore.sendTrackingData({
        locationId: locationId.value,
        informationId: currentInformation.value.id,
        kind: currentInformation.value.kind,
        action: TRACKING_EVENT.CLICK
    })
}

onMounted(sendTracking)

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createBlock(ModalControllerBase, { contentClass: "light-light-background purple-purple-color p-relative" }, {
    header: _withCtx(() => [
      _createVNode(CustomHeader, {
        "back-button": "",
        title: _ctx.$t(`titleRoute.${currentInformation.value?.kind ?? _unref(INFORMATION_TYPE).PROMOTION}`)
      }, {
        start: _withCtx(() => [
          _createVNode(_unref(IonButton), {
            onClick: _cache[0] || (_cache[0] = ($event: any) => (_unref(modalController).dismiss())),
            class: "purple-light-color"
          }, {
            default: _withCtx(() => [
              _createVNode(_unref(IonIcon), { icon: _unref(chevronBack) }, null, 8, ["icon"]),
              _createTextVNode(" " + _toDisplayString(_ctx.$t('button.back')), 1)
            ]),
            _: 1
          })
        ]),
        _: 1
      }, 8, ["title"])
    ]),
    default: _withCtx(() => [
      (!_unref(isApp)() && !__props.noButtons)
        ? (_openBlock(), _createBlock(ButtonNavigations, {
            key: 0,
            "is-button-back": isButtonBack.value,
            "is-button-forward": isButtonForward.value,
            onFoward: goFoward,
            onBack: goBack
          }, null, 8, ["is-button-back", "is-button-forward"]))
        : _createCommentVNode("", true),
      (currentInformation.value)
        ? (_openBlock(), _createBlock(InformationInterface, {
            key: 1,
            information: currentInformation.value,
            "item-placeholder": __props.itemPlaceholder,
            cart: "",
            onAdd: _cache[1] || (_cache[1] = ($event: any) => (addToCart($event))),
            onAddPackage: _cache[2] || (_cache[2] = ($event: any) => (addInformationPackageToCart($event)))
          }, null, 8, ["information", "item-placeholder"]))
        : _createCommentVNode("", true)
    ]),
    _: 1
  }))
}
}

})